import { TabPanel, TabView } from "primereact/tabview";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CustomLoader } from "../../../shared/components/custom-loader/custom-loader";
import { Icon } from "../../../shared/icons/icon";
import { SpecialLabelCases } from "../../../shared/models/utilities";
import { CustomModal } from "../../../shared/components/custom-modal/custom-modal";
import { SERVICES } from "../../../core/services/services";
import { HELPER } from "../../../shared/helper/helper";
import { DebitMandateTokens } from "./mandate-tokens";
import { CancelMandateConfirmation } from "./cancel-mandate-confirmation";
import { BACKOFFICE_API } from "../../../core/apis/backoffice-apis";
import { Optional } from "../optional/optional";
import { FRONTOFFICE_API } from "../../../core/apis/frontoffice-apis";
import { ACTIVATED, UserTypes } from "../../constants";
import { DownloadConfirmation } from "../download-confirmation/download-confirmaton";
import { saveAs } from "file-saver";
import { Toast } from "primereact/toast";

export function MandateDetails(props) {
  const toast = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [currentModalIndex, setCurrentModalIndex] = useState(0);
  const [error, setError] = useState(null);
  const [detailsContent, setDetailsContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mandateData, setMandateData] = useState(null);
  const [mandateStatus, setMandateStatus] = useState();
  const [mandateReference, setMandateReference] = useState();
  const [shouldReloadOnGoingBack, setShouldReloadOnGoingBack] = useState(false);
  const [mandateCancellationType, setMandateCancellationType] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [downloadingMandate, setDownloadingMandate] = useState(false);
  const shouldFetchMandate = useRef(true);

  const handleError = useCallback(async (error) => {
    const errMessage = await HELPER.PARSE_BLOB_ERROR(error);
    setDownloadError(errMessage);
    setDownloadingMandate(false);
  }, []);

  const downloadMandateDetails = useCallback(() => {
    setDownloadingMandate(true);
    setCurrentIndex(0);
    const frontOfficeUrl = FRONTOFFICE_API.DEBIT_MANDATES;
    const backOfficeUrl = BACKOFFICE_API.DEBIT_MANDATES;
    const url =
      props.userType === UserTypes.Frontoffice ? frontOfficeUrl : backOfficeUrl;
    const isBlob = true;
    let mandateRef = mandateReference;
    SERVICES.MAKE_GET_REQUEST(
      `${url}/download-mandate-pdf/${mandateRef}`,
      null,
      isBlob
    )
      .then((data) => {
        saveAs(data, `Mandate-${mandateRef}.pdf`);
        setVisible(false);
        toast.current.show({
          severity: "success",
          summary: "",
          detail: "File Download is in progress!",
          life: 10000,
        });
        setDownloadError(null);
        setDownloadingMandate(false);
      })
      .catch((error) => {
        handleError(error);
      });
  }, [mandateReference, handleError, props.userType]);

  const getMandateByReference = useCallback(
    (reference) => {
      setLoading(true);
      setCurrentIndex(0);
      const frontOfficeUrl = FRONTOFFICE_API.DEBIT_MANDATES;
      const backOfficeUrl = BACKOFFICE_API.DEBIT_MANDATES;
      const url =
        props.userType === UserTypes.Frontoffice
          ? frontOfficeUrl
          : backOfficeUrl;
      SERVICES.MAKE_GET_REQUEST(
        `${url}/${reference ? reference : mandateReference}`
      )
        .then((data) => {
          const result = data.result;
          setMandateData(result);
          setMandateStatus(result?.status);
          setMandateReference(result?.reference);
          setDetailsContent(HELPER.GET_DETAILS_ARRAY(result));
          setError(null);
          setLoading(false);
        })
        .catch((error) => {
          setError(HELPER.PROCESS_ERROR(error));
          setLoading(false);
        });
    },
    [mandateReference, props.userType]
  );

  useEffect(() => {
    if (shouldFetchMandate.current) {
      getMandateByReference(props.mandateReference);
      shouldFetchMandate.current = false;
    }
  }, [getMandateByReference, props.mandateReference]);

  function closeModal(options) {
    const reload = typeof options === "object" ? options?.reload : options;
    const updatedData = typeof options === "object" ? options?.afterData : null;

    if (props.mobile) {
      setCurrentIndex(0);
    } else {
      setVisible(false);
    }

    if (updatedData) {
      try {
        const parsed = typeof updatedData === "string" ? JSON.parse(updatedData) : updatedData;
        const updatedStatus = parsed?.status;
        if (updatedStatus) {
          const merged = { ...mandateData, status: updatedStatus };
          setMandateData(merged);
          setMandateStatus(updatedStatus);
          setDetailsContent(HELPER.GET_DETAILS_ARRAY(merged));
        }
      } catch {
        // afterData could not be parsed — leave details unchanged
      }
    }

    if (reload === true) {
      setShouldReloadOnGoingBack(true);
      getMandateByReference();
    }
  }

  function openModal(index) {
    setCurrentModalIndex(index);
    setVisible(true);
  }

  const transformView = (itemCase, value) => {
    let result = value;
    if (itemCase) {
      SpecialLabelCases.forEach((e) => {
        if (itemCase === "status" && value.toLowerCase() === "started") {
          result = HELPER.SETTLEMENT_WARNING_STATUS(value);
        } else if (e.case === itemCase) {
          result = e.action(value);
        }
      });
    }
    return result;
  };

  const detailsView = () => {
    return detailsContent.map((details, index) => (
      <div key={index.toString()} className="p-grid dcir-row">
        <div className="p-col-6">
          <p className="details-label">{details.label}:</p>
        </div>
        <div className="p-col-6">
          <p className="details-value">
            {transformView(details.itemCase, details.value, details.type)}
          </p>
        </div>
      </div>
    ));
  };

  const errorView = () => {
    return (
      <div>
        <div className="empty-container">
          <div className="p-text-center">
            <Icon icon="error-message-icon" />
          </div>
          <div>
            <p className="empty-text">{error}</p>
          </div>
          <div className="p-mt-3">
            <div style={{ maxWidth: "200px", margin: "0 auto" }}>
              <button
                className="primary-button"
                onClick={getMandateByReference}
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const modalContent = () => {
    // eslint-disable-next-line default-case
    switch (currentModalIndex) {
      case 0:
        return (
          <CancelMandateConfirmation
            closeModal={closeModal}
            mandateReference={mandateReference}
            userType={props.userType}
            cancellationType={mandateCancellationType}
          />
        );
      case 1:
        return (
          <DownloadConfirmation
            error={downloadError}
            onDownload={downloadMandateDetails}
            loading={downloadingMandate}
            closeModal={closeModal}
            userType={props.userType}
          />
        );
      default:
        break;
    }
  };

  const canCancelMandate = () => {
    const cancellationTerms = mandateData?.cancellationTerms;
    if (mandateStatus !== ACTIVATED) {
      return false;
    }
    if (
      props.userType === UserTypes.Frontoffice &&
      cancellationTerms.includes("MERCHANT")
    ) {
      return true;
    }
    if (
      props.userType === UserTypes.Backoffice &&
      cancellationTerms.includes("ISSUER")
    ) {
      return true;
    }
    return false;
  };

  const mandateTabView = () => {
    return (
      <div
        style={{ backgroundColor: "#ffffff", paddingTop: "1em" }}
        className="p-shadow-1 p-pb-5"
      >
        <div className="p-text-left p-ml-3 p-mt-2 p-pb-3">
          <span
            onClick={() => props?.goBack(shouldReloadOnGoingBack)}
            className="add-cursor"
          >
            <span>
              <Icon icon="go-back-icon" />
            </span>
          </span>
        </div>
        <TabView>
          <TabPanel header="Mandate Details">
            <div className="p-text-left">
              <Optional show={!loading}>
                <div className="p-grid p-mt-2">
                  <div
                    className={canCancelMandate() ? "p-col-8" : "p-col-10"}
                  />
                  <div className="p-col-2">
                    <Optional show={mandateData?.fileLink}>
                      <button
                        onClick={() => openModal(1)}
                        className="download-button"
                        style={{ fontSize: "0.75rem", width: "100%" }}
                      >
                        <i className="pi pi-download"></i> Download
                      </button>
                    </Optional>
                  </div>
                  <Optional show={canCancelMandate()}>
                    <div className="p-col-2">
                      <button
                        onClick={() => openModal(0)}
                        className="primary-button"
                        style={{ fontSize: "0.9rem" }}
                      >
                        Cancel Mandate
                      </button>
                    </div>
                  </Optional>
                </div>
              </Optional>
              <Optional show={!loading && !error}>
                <div className="centralize">{detailsView()}</div>
                {mandateStatus === ACTIVATED &&
                  props.userType === UserTypes.Backoffice && (
                    <div className="p-d-flex p-justify-end p-mt-5">
                      <button
                        style={{ width: "10rem", background: "transparent" }}
                        className="bare-butto error-text secondary-button"
                        onClick={() => {
                          setMandateCancellationType("force");
                          openModal(0);
                        }}
                      >
                        Force Cancel
                      </button>
                    </div>
                  )}
              </Optional>
              <Optional show={loading}>
                <div style={{ margin: "10rem 0" }} className="p-text-center">
                  <CustomLoader loadingText="Loading..." />
                </div>
              </Optional>
              <Optional show={error && !loading}>{errorView()}</Optional>
            </div>
          </TabPanel>
          <TabPanel header="Mandate Tokens">
            <div className="centralize">
              <DebitMandateTokens
                reference={mandateReference}
                userType={props.userType}
              />
            </div>
          </TabPanel>
        </TabView>
      </div>
    );
  };

  const mandateDetailsView = () => {
    // eslint-disable-next-line default-case
    switch (currentIndex) {
      case 0:
        return mandateTabView();
      case 1:
        return errorView();
    }
  };

  return (
    <div>
      <Toast ref={toast} />
      <div>
        <CustomModal
          onHide={closeModal}
          visible={visible}
          modalContent={modalContent}
          closeModal={closeModal}
        />
      </div>
      {mandateDetailsView()}
    </div>
  );
}
